import { useId, useState, type FormEvent } from 'react';
import {
  BUTTON_CLASS,
  DetailDrawer,
  fieldStyle,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import { createSpace } from '@/lib/api';

/**
 * Open a room — one field, one request (CON-22, CON-5, ADR-0071).
 *
 * **This is half of `ConnectPanel`, and the split killed a real defect.** That
 * panel made two calls behind one button — `POST /spaces`, then
 * `POST /spaces/{id}/members` — and it had to name the room *before* the API had
 * resolved the invited address's domain to an agency. So it seeded the title
 * from `SpaceList`'s `namesByDomain` map where it happened to already know a
 * name and **from the bare domain otherwise**: inviting an agency the reader had
 * never shared a space with produced a room called `smpd.la.gov`. There was
 * nothing to fix in that lookup — the client cannot resolve a domain, and the
 * one thing that can was the request being made after the name was needed.
 *
 * A space exists on its own, so the name is asked for and the room is created
 * with one member: the agency that made it, `Active` at once, because there is
 * no handshake with yourself (CON-22). `AddAgencyPanel` is the other half.
 *
 * **With it went the two-call flow, its half-done error copy, and
 * `namesByDomain`.** `CONNECT_COPY.errors.partial` named the empty room an admin
 * had just made without meaning to; there is no partial outcome left to name,
 * because there is only one call. Nothing here compensates for anything, and
 * there is still no delete-space act in the product (SPEC §8 has no
 * `space.deleted`, deliberately — EXIT-4 names the orphaned-room question as
 * open and this slice does not answer it).
 *
 * CON-7a's subscription gate is on this request, so a plan-less agency is turned
 * away here and the API's own message is what says so — it names what the agency
 * can still do, which "the space could not be created" would throw away.
 *
 * Built on `DetailDrawer`, which is already a modal dialog with a focus trap,
 * Escape handling, and focus restore. Nothing here re-implements any of that.
 */

export interface CreateSpacePanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called once the room exists, with **the id the API assigned it**.
   *
   * The caller refetches on this — never patches (§4) — and then opens the new
   * room's members panel, which is where the next act is. The id is what makes
   * that possible; this panel used to report success in words and leave the
   * admin to find the roster themselves.
   */
  onCreated: (spaceId: string) => void;
}

export function CreateSpacePanel({ open, onClose, onCreated }: CreateSpacePanelProps) {
  const nameId = useId();
  const nameHintId = useId();

  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /**
   * `void`-returning, with the async work in a nested function.
   *
   * A submit handler that returns a promise is a floating promise as far as
   * React is concerned — nothing awaits it, so a rejection escaping it would be
   * unhandled. Every failure path below is caught, and the signature says so.
   */
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;

    const wanted = name.trim();
    if (wanted === '') {
      setError(SPACE_LIST_COPY.createSpace.errors.nameRequired);
      return;
    }

    void send(wanted);
  }

  async function send(wanted: string): Promise<void> {
    setPending(true);
    setError(undefined);

    try {
      const space = await createSpace(wanted);
      setName('');
      // The id off the wire — the API owns what it stored, which is the same
      // reason the success line used to report `space.name` rather than the
      // string that was typed. That line is gone: this panel hands over to the
      // new room's roster now instead of reporting to a reader still sitting
      // on the form.
      onCreated(space.id);
    } catch (caught) {
      // CON-7a's refusal lands here, and it is the API's own wording.
      setError(messageFrom(caught, SPACE_LIST_COPY.createSpace.errors.failed));
    } finally {
      setPending(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={SPACE_LIST_COPY.createSpace.title}
      closeLabel={SPACE_LIST_COPY.createSpace.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {SPACE_LIST_COPY.createSpace.intro}
        </p>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={nameId} className="text-xs font-medium">
            {SPACE_LIST_COPY.createSpace.label}
          </label>
          <input
            id={nameId}
            name="name"
            type="text"
            value={name}
            aria-describedby={nameHintId}
            onChange={(event) => {
              setName(event.target.value);
            }}
            className="border px-3 py-2 text-sm"
            style={fieldStyle()}
          />
          <span id={nameHintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {SPACE_LIST_COPY.createSpace.hint}
          </span>
        </div>

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className={BUTTON_CLASS}
            style={primaryButtonStyle()}
          >
            {pending ? SPACE_LIST_COPY.createSpace.submitting : SPACE_LIST_COPY.createSpace.submit}
          </button>

          <button
            type="button"
            onClick={onClose}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {SPACE_LIST_COPY.createSpace.cancel}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
